/**
 * Companion script for the modern cart drawer. Two independent jobs:
 *
 *   1. The upsell variant picker — always active. An upsell product with more
 *      than one available variant opens a sheet to choose a size/option before
 *      being added.
 *   2. Free-shipping unlock confetti — only when Theme settings > Cart >
 *      "Confetti on free shipping unlock" is ticked, read from
 *      window.cartDrawerV2.confetti.
 *
 * Markup: snippets/cart-drawer-v2.liquid, styles: assets/component-cart-drawer-v2.css.
 *
 * This file owns nothing but the celebration. The drawer's behaviour — opening,
 * closing, quantity changes, removals, discount codes, cross-sell adds, section
 * re-renders — is all still assets/cart.js, assets/cart-drawer.js and
 * assets/shipping-insurance.js, exactly as in the classic drawer.
 *
 * Both hooks are MutationObservers rather than pub/sub subscriptions on purpose:
 * `PUB_SUB_EVENTS.cartUpdate` is published at different points relative to the
 * section swap depending on which code path mutated the cart (cart.js publishes
 * after rendering, cart-drawer.js's discount handler publishes before), so a
 * subscriber cannot reliably read the freshly rendered markup. Watching the DOM
 * for the swap itself has no such ordering problem.
 */
(function () {
  'use strict';

  // Shrimpy brand ramp: blue, cyan and navy plus two tints, so the burst reads as
  // the brand celebrating rather than generic party graphics. Pure white is left
  // out — it is invisible on the drawer's white card.
  var COLORS = ['#0682f5', '#27cce6', '#1d3144', '#6fb8fb', '#9ee7f4'];

  var PARTICLE_COUNT = 90;
  var MAX_FRAMES = 240; // ~4s at 60fps
  var RENDER_SETTLE_MS = 60;

  var wasUnlocked = null;
  var animationId = null;
  var settleTimer = null;

  function config() {
    return window.cartDrawerV2 || {};
  }

  function t(key, fallback) {
    var strings = config().strings || {};
    return strings[key] || fallback;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function drawer() {
    return document.querySelector('cart-drawer.cartv2');
  }

  /** null when the tracker is switched off or not rendered (e.g. empty cart). */
  function readUnlockedState() {
    var msg = document.querySelector('[data-cartv2-shipping-msg]');
    if (!msg) return null;
    return msg.dataset.unlocked === 'true';
  }

  function fire() {
    // Merchant setting. Only an explicit false disables it, so missing config
    // (older cached markup) keeps the previous behaviour.
    if (config().confetti === false) return;
    if (prefersReducedMotion()) return;

    var host = drawer();
    if (!host) return;

    // Re-queried every time: the canvas lives inside #CartDrawer and is replaced
    // wholesale by every section render, so a cached reference goes stale.
    var canvas = host.querySelector('[data-cartv2-confetti]');
    if (!canvas || typeof canvas.getContext !== 'function') return;

    var card = host.querySelector('.cartv2__card') || canvas.parentElement;
    var width = (card && card.clientWidth) || 440;
    var height = (card && card.clientHeight) || window.innerHeight;
    if (width <= 0 || height <= 0) return;

    canvas.width = width;
    canvas.height = height;

    var ctx = canvas.getContext('2d');
    var particles = [];
    var i;

    for (i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * width,
        // Staggered above the card so the fall arrives as a shower, not a wall.
        y: -15 - Math.random() * 120,
        width: 5 + Math.random() * 6,
        height: 9 + Math.random() * 7,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        vx: (Math.random() - 0.5) * 2.2,
        vy: 2.6 + Math.random() * 3.8,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        oscillationSpeed: 0.03 + Math.random() * 0.05,
        oscillationAmplitude: 1.2 + Math.random() * 1.8,
      });
    }

    if (animationId) cancelAnimationFrame(animationId);

    var frame = 0;

    function step() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      var alive = 0;

      particles.forEach(function (p) {
        if (p.y > canvas.height + 40) return;
        alive++;

        p.y += p.vy;
        p.x += p.vx + Math.sin(frame * p.oscillationSpeed) * p.oscillationAmplitude;
        p.rotation += p.rotationSpeed;

        ctx.save();
        ctx.translate(p.x + p.width / 2, p.y + p.height / 2);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        // Fade the last third of the run out so the burst ends rather than stops.
        ctx.globalAlpha = frame > MAX_FRAMES * 0.66 ? Math.max(0, 1 - (frame - MAX_FRAMES * 0.66) / (MAX_FRAMES * 0.34)) : 1;
        ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
        ctx.restore();
      });

      if (alive > 0 && frame < MAX_FRAMES) {
        animationId = requestAnimationFrame(step);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        animationId = null;
      }
    }

    animationId = requestAnimationFrame(step);
  }

  /**
   * Fires only on the locked -> unlocked transition, never on every render.
   * `wasUnlocked` starts as null so the very first reading only records the
   * baseline: a shopper opening a drawer that was already over the threshold gets
   * the burst from checkOpenState() instead, not from this.
   */
  function checkTransition() {
    var isUnlocked = readUnlockedState();
    if (isUnlocked === null) return;

    if (isUnlocked && wasUnlocked === false) fire();
    wasUnlocked = isUnlocked;
  }

  /** Celebrate on open when the cart is already over the threshold. */
  function checkOpenState() {
    if (readUnlockedState() === true) fire();
  }

  function scheduleTransitionCheck() {
    if (settleTimer) clearTimeout(settleTimer);
    // A section swap replaces #CartDrawer's innerHTML in one go, but Dawn also
    // toggles classes and re-binds afterwards; a short settle keeps this to one
    // read per mutation burst.
    settleTimer = setTimeout(checkTransition, RENDER_SETTLE_MS);
  }


  /* ------------------------------------------------------- variant picker */

  /**
   * Upsell cards post a plain form that assets/cart-drawer.js already knows how
   * to submit (submitCrossSellForm: /cart/add.js with the drawer sections, then
   * renderContents). The picker therefore does NOT add to the cart itself — it
   * writes the chosen variant id into that same form's hidden input and calls
   * requestSubmit(), so the add, the re-render, the shipping-insurance re-tier and
   * the open drawer all behave exactly as a single-variant add already does. One
   * code path, no duplicated cart logic.
   */

  var productCache = {};
  var activeForm = null;

  function sheet() {
    var host = drawer();
    return host ? host.querySelector('[data-variant-sheet]') : null;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatMoney(cents) {
    var amount = Number(cents);
    if (!isFinite(amount)) return '';
    // Use the theme's own formatter where available so the shop's money_format is
    // honoured rather than a dollar sign being assumed.
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      try {
        return Shopify.formatMoney(amount, window.moneyFormat);
      } catch (e) {
        /* fall through */
      }
    }
    return '$' + (amount / 100).toFixed(2);
  }

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) {
        if (!res.ok) throw new Error('product-fetch-failed');
        return res.json();
      })
      .then(function (data) {
        productCache[handle] = data;
        return data;
      });
  }

  /** Variant whose option values match the current selection exactly, or null. */
  function matchVariant(product, chosen) {
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var hit = true;
      for (var j = 0; j < chosen.length; j++) {
        if (String(v.options[j]) !== String(chosen[j])) {
          hit = false;
          break;
        }
      }
      if (hit) return v;
    }
    return null;
  }

  function readSelection(panel) {
    return [].map.call(panel.querySelectorAll('[data-variant-option]'), function (sel) {
      return sel.value;
    });
  }

  /**
   * Reflects the current selection into the sheet: live price, and whether adding
   * is allowed. On a multi-option product a chosen combination may not resolve to
   * an available variant at all, so the button is disabled and relabelled instead
   * of silently posting a bad id.
   */
  function syncSheet(panel, product) {
    var confirm = panel.querySelector('[data-variant-confirm]');
    var priceEl = panel.querySelector('[data-variant-price]');
    var variant = matchVariant(product, readSelection(panel));

    if (priceEl) {
      var html = '';
      if (variant) {
        html = '<span class="cartv2__variant-price-now">' + escapeHtml(formatMoney(variant.price)) + '</span>';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html += ' <s class="cartv2__variant-price-was">' + escapeHtml(formatMoney(variant.compare_at_price)) + '</s>';
        }
      }
      priceEl.innerHTML = html;
    }

    if (confirm) {
      var ok = !!(variant && variant.available);
      confirm.disabled = !ok;
      confirm.dataset.variantId = variant ? variant.id : '';
      confirm.textContent = ok
        ? t('addToCart', 'Add to cart')
        : variant
          ? t('soldOut', 'Sold out')
          : t('unavailable', 'Unavailable');
    }
  }

  function closeSheet() {
    var el = sheet();
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    var panel = el.querySelector('[data-variant-sheet-panel]');
    if (panel) panel.innerHTML = '';
    activeForm = null;
  }

  /** Writes the chosen variant into the originating card's form and submits it. */
  function submitWithVariant(variantId) {
    if (!activeForm || !variantId) return;
    var form = activeForm;
    var input = form.querySelector('[data-cross-sell-variant-id]');
    if (input) input.value = variantId;
    var submitter = form.querySelector('[data-cross-sell-add]');
    closeSheet();
    // requestSubmit fires a submit event that bubbles to <cart-drawer>, whose
    // onDrawerSubmit hands it to submitCrossSellForm. form.submit() would skip the
    // event entirely and hard-navigate to /cart.
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  function optionNamesOf(product) {
    // product.options is an array of strings on the .js endpoint in some theme
    // versions and an array of objects in others; normalise to names.
    return (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });
  }

  function openSheet(button) {
    var el = sheet();
    var panel = el && el.querySelector('[data-variant-sheet-panel]');
    if (!panel) return;

    var handle = button.dataset.productHandle;
    if (!handle) return;

    // Remember which card asked, so Confirm submits that card's own form.
    activeForm = button.closest('[data-cart-drawer-cross-sell-form]');

    button.classList.add('loading');
    fetchProduct(handle)
      .then(function (product) {
        var available = (product.variants || []).filter(function (v) {
          return v.available;
        });

        // Nothing left to choose (stock moved since the drawer rendered): add the
        // one available variant directly rather than showing a one-option sheet.
        if (available.length <= 1) {
          if (available.length === 1) submitWithVariant(available[0].id);
          return;
        }

        var names = optionNamesOf(product);
        var fields = names
          .map(function (name, index) {
            var seen = {};
            var values = [];
            available.forEach(function (v) {
              var value = v.options[index];
              if (value != null && !seen[value]) {
                seen[value] = true;
                values.push(value);
              }
            });
            var id = 'CartDrawerV2-VariantOption-' + index;
            return (
              '<div class="cartv2__variant-field">' +
              '<label class="cartv2__variant-label" for="' + id + '">' + escapeHtml(name) + '</label>' +
              '<select class="cartv2__variant-select" id="' + id + '" data-variant-option="' + index + '">' +
              values
                .map(function (v) {
                  return '<option value="' + escapeHtml(v) + '">' + escapeHtml(v) + '</option>';
                })
                .join('') +
              '</select>' +
              '</div>'
            );
          })
          .join('');

        var image = product.featured_image || (product.images && product.images[0]) || '';
        var imageHtml = image
          ? '<span class="cartv2__variant-img" style="background-image: url(' +
            escapeHtml(JSON.stringify(String(image))) +
            ');"></span>'
          : '';

        panel.innerHTML =
          '<button type="button" class="cartv2__variant-close" data-variant-close aria-label="' +
          escapeHtml(t('close', 'Close')) +
          '">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
          '</button>' +
          '<div class="cartv2__variant-head">' +
          imageHtml +
          '<span class="cartv2__variant-head-text">' +
          '<span class="cartv2__variant-title" id="CartDrawerV2-VariantSheet-Title">' +
          escapeHtml(product.title) +
          '</span>' +
          '<span class="cartv2__variant-price" data-variant-price></span>' +
          '</span>' +
          '</div>' +
          fields +
          '<button type="button" class="cartv2__variant-confirm cartv2__checkout-btn" data-variant-confirm></button>';

        // Open on the first available variant so the sheet never starts on a
        // sold-out combination.
        var first = available[0];
        [].forEach.call(panel.querySelectorAll('[data-variant-option]'), function (sel, index) {
          var want = first.options[index];
          if (want != null) sel.value = want;
        });

        panel.dataset.handle = handle;
        syncSheet(panel, product);

        el.classList.add('is-open');
        el.setAttribute('aria-hidden', 'false');
        var firstSelect = panel.querySelector('.cartv2__variant-select');
        if (firstSelect) firstSelect.focus();
      })
      .catch(function (err) {
        console.error('Cart drawer variant picker:', err);
        // Never dead-end: fall back to the form's default (first available) variant.
        var form = activeForm;
        if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit(form.querySelector('[data-cross-sell-add]'));
        }
      })
      .then(function () {
        button.classList.remove('loading');
      });
  }

  function bindVariantPicker() {
    // Delegated on the document: every cart mutation replaces the whole of
    // #CartDrawer, so listeners bound to cards or to the sheet are orphaned.
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var addBtn = target.closest('[data-cross-sell-add]');
      if (addBtn) {
        var count = parseInt(addBtn.dataset.variantCount, 10) || 1;
        // Single variant: leave the normal form submit completely alone.
        if (count <= 1) return;
        event.preventDefault();
        openSheet(addBtn);
        return;
      }

      if (target.closest('[data-variant-close]')) {
        event.preventDefault();
        closeSheet();
        return;
      }

      var confirmBtn = target.closest('[data-variant-confirm]');
      if (confirmBtn) {
        event.preventDefault();
        if (!confirmBtn.disabled) submitWithVariant(confirmBtn.dataset.variantId);
        return;
      }

      // Scrim click closes; a click inside the panel must not.
      var sheetEl = target.closest('[data-variant-sheet]');
      if (sheetEl && !target.closest('[data-variant-sheet-panel]')) {
        closeSheet();
      }
    });

    document.addEventListener('change', function (event) {
      var sel = event.target;
      if (!sel || !sel.matches || !sel.matches('[data-variant-option]')) return;
      var panel = sel.closest('[data-variant-sheet-panel]');
      if (!panel) return;
      var product = productCache[panel.dataset.handle];
      if (product) syncSheet(panel, product);
    });

    // Capture phase so the sheet closes before <cart-drawer>'s own Escape handler
    // closes the whole drawer underneath it.
    document.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'Escape') return;
        var el = sheet();
        if (el && el.classList.contains('is-open')) {
          event.stopPropagation();
          closeSheet();
        }
      },
      true
    );
  }

  function init() {
    var host = drawer();
    if (!host) return;

    // Bound first and independently of the confetti observers below: the picker
    // has to work with the free-shipping tracker switched off and on an empty cart.
    bindVariantPicker();

    wasUnlocked = readUnlockedState();

    var root = host.querySelector('#CartDrawer');
    if (root) {
      // childList is the real signal — every cart mutation re-renders the drawer
      // by replacing innerHTML. `data-unlocked` is watched as well, narrowed by
      // attributeFilter so this stays quiet through the aria-busy and disabled
      // toggles assets/shipping-insurance.js makes on every sync.
      new MutationObserver(scheduleTransitionCheck).observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-unlocked'],
      });
    }

    // assets/cart-drawer.js adds `active` to the host to open the drawer.
    var wasOpen = host.classList.contains('active');
    new MutationObserver(function () {
      var isOpen = host.classList.contains('active');
      if (isOpen && !wasOpen) checkOpenState();
      wasOpen = isOpen;
    }).observe(host, { attributes: true, attributeFilter: ['class'] });

    if (wasOpen) checkOpenState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
